import { useState, useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  AlertCircle,
  Loader2,
  FileText,
  Clock,
  CheckCircle,
  Send,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { toast } from "sonner";
import {
  useTimeEntryShifts,
  type WeekKey,
} from "@/features/time-entry/hooks/useTimeEntryShifts";
import { useSubmittedTimeEntries } from "@/features/time-entry/hooks/useSubmittedTimeEntries";
import { TimeEntryWeekForm } from "@/features/time-entry/components/TimeEntryWeekForm";
import { TimeEntryWeekSelector } from "@/features/time-entry/components/TimeEntryWeekSelector";
import { SubmittedTimeEntryCard } from "@/features/time-entry/components/SubmittedTimeEntryCard";
import {
  getWeekKey,
  getPreviousWeekKey,
  getNextWeekKey,
  formatWeekRange,
  isCurrentWeek,
} from "@/features/time-entry/utils/week-utils";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { BatchExpenseSubmitDialog } from "@/features/expense/components/BatchExpenseSubmitDialog";
import { ExpenseCard } from "@/features/expense/components/ExpenseCard";
import { ExpenseEditDialog } from "@/features/expense/components/ExpenseEditDialog";
import {
  useProviderExpenses,
  type ExpenseItem,
} from "@/features/expense/hooks/useProviderExpenses";
import { useDeleteExpense } from "@/features/expense/hooks/useDeleteExpense";
import { AdHocTimeEntryDialog } from "@/features/time-entry/components/AdHocTimeEntryDialog";

type TabType = "needs-submission" | "pending" | "history";

function TimesheetsPage() {
  const [activeTab, setActiveTab] = useState<TabType>("needs-submission");
  const [historyWeek, setHistoryWeek] = useState<WeekKey>(
    getWeekKey(new Date()),
  );

  const {
    weeklyGroups,
    sortedWeekKeys,
    currentWeekKey,
    isLoading: isLoadingNeeds,
    error: needsError,
    refetch: refetchNeeds,
  } = useTimeEntryShifts();

  const {
    weeklyGroups: pendingGroups,
    totalPending,
    isLoading: isLoadingPending,
    refetch: refetchPending,
  } = useSubmittedTimeEntries("pending");

  // Pending expenses for the pending review tab
  const {
    expenseGroups: pendingExpenseGroups,
    isLoading: _isLoadingPendingExpenses,
    totalPending: totalPendingExpenses,
  } = useProviderExpenses({ status: "pending" });

  // Rejected expenses for the needs submission tab (require provider action)
  const {
    expenseGroups: rejectedExpenseGroups,
    isLoading: _isLoadingRejectedExpenses,
    totalRejected: totalRejectedExpenses,
  } = useProviderExpenses({ status: "rejected" });

  const [isExpenseDialogOpen, setIsExpenseDialogOpen] = useState(false);
  const [isAdHocDialogOpen, setIsAdHocDialogOpen] = useState(false);

  // Withdraw mutation
  const trpc = useTRPC();
  const [withdrawingShiftId, setWithdrawingShiftId] = useState<string | null>(
    null,
  );
  const { mutate: withdrawEntry } = useMutation(
    trpc.timeEntry.withdrawPendingTimeEntry.mutationOptions({
      onSuccess: (result) => {
        toast.success(
          result.adHocShiftRetired
            ? "Added hours withdrawn successfully."
            : "Time entry withdrawn successfully. You can now resubmit.",
        );
        refetchPending();
        refetchNeeds();
        setWithdrawingShiftId(null);
        if (!result.adHocShiftRetired) {
          setActiveTab("needs-submission");
        }
      },
      onError: (error) => {
        toast.error(error.message || "Failed to withdraw time entry");
        setWithdrawingShiftId(null);
      },
    }),
  );

  // Expense delete mutation
  const [deletingExpenseId, setDeletingExpenseId] = useState<string | null>(
    null,
  );
  const { mutateAsync: deleteExpense } = useDeleteExpense();
  const handleDeleteExpense = async (expenseId: string) => {
    setDeletingExpenseId(expenseId);
    try {
      await deleteExpense({ expenseId });
    } finally {
      setDeletingExpenseId(null);
    }
  };

  // Expense edit dialog
  const [editingExpense, setEditingExpense] = useState<ExpenseItem | null>(
    null,
  );
  const handleEditExpense = (expense: ExpenseItem) => {
    setEditingExpense(expense);
  };

  // Load only the selected week for history (approved entries)
  const { weeklyGroups: historyGroups, isLoading: isLoadingHistory } =
    useSubmittedTimeEntries("approved", historyWeek);

  // Default to current week if it has shifts, otherwise first available week
  const defaultWeek = useMemo(() => {
    if (sortedWeekKeys.length === 0) {
      return currentWeekKey;
    }
    if (sortedWeekKeys.includes(currentWeekKey)) {
      return currentWeekKey;
    }
    return sortedWeekKeys[0];
  }, [sortedWeekKeys, currentWeekKey]);

  const [selectedWeek, setSelectedWeek] = useState<WeekKey>(defaultWeek);

  // Find the selected week's group
  const selectedGroup = useMemo(() => {
    return weeklyGroups.find((g) => g.weekKey === selectedWeek);
  }, [weeklyGroups, selectedWeek]);

  // Update selected week when data changes
  useMemo(() => {
    if (sortedWeekKeys.length > 0 && !sortedWeekKeys.includes(selectedWeek)) {
      setSelectedWeek(sortedWeekKeys[0]);
    }
  }, [sortedWeekKeys, selectedWeek]);

  // Derive default timezone from available shifts for timezone-aware week calculations
  const defaultTimezone = useMemo(() => {
    for (const group of weeklyGroups) {
      if (group.shifts.length > 0) {
        return group.shifts[0].timezoneId;
      }
    }
    return undefined;
  }, [weeklyGroups]);

  // Only show initial loading spinner for needs and pending (history loads per-week)
  const isInitialLoading = isLoadingNeeds || isLoadingPending;

  if (isInitialLoading) {
    return (
      <div className="flex flex-col justify-center items-center h-60 text-center p-6">
        <Loader2 className="w-10 h-10 text-primary animate-spin mb-4" />
        <p className="text-lg font-medium text-slate-700">
          Loading Timesheets...
        </p>
        <p className="text-sm text-slate-500">Please wait a moment.</p>
      </div>
    );
  }

  if (needsError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <div className="flex flex-col items-center justify-center text-center">
          <AlertCircle className="w-10 h-10 text-red-500 mb-3" />
          <p className="text-lg font-semibold text-red-700">
            Unable to Load Timesheets
          </p>
          <p className="text-sm text-red-600 mt-1">
            We encountered an error while loading your timesheets.
          </p>
          <Button
            variant="default"
            size="sm"
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Refresh
          </Button>
        </div>
      </div>
    );
  }

  const tabs = [
    {
      id: "needs-submission" as const,
      label: "Needs Submission",
      icon: <Send className="h-4 w-4" />,
      count:
        weeklyGroups.reduce((sum, g) => sum + g.totalShifts, 0) +
        totalRejectedExpenses,
    },
    {
      id: "pending" as const,
      label: "Pending Review",
      icon: <Clock className="h-4 w-4" />,
      count: totalPending + totalPendingExpenses,
    },
    {
      id: "history" as const,
      label: "History",
      icon: <CheckCircle className="h-4 w-4" />,
      // No count shown - history loads per-week for performance
      count: 0,
    },
  ];

  return (
    <div className="max-w-3xl mx-auto">
      {/* Tab Navigation */}
      <div className="mb-6 flex gap-1 rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            aria-label={tab.label}
            aria-pressed={activeTab === tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              "flex min-h-12 flex-1 items-center justify-center gap-2 rounded-xl px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary",
              activeTab === tab.id
                ? "bg-primary/5 text-primary"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-50",
            )}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
            {tab.count > 0 && (
              <Badge
                variant="secondary"
                className={cn(
                  "ml-1 h-5 min-w-[20px] px-1.5",
                  activeTab === tab.id
                    ? "bg-primary/10 text-primary"
                    : "bg-slate-200 text-slate-600",
                )}
              >
                {tab.count}
              </Badge>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "needs-submission" && (
        <>
          {/* Add Hours and Expense Buttons */}
          <div className="mb-4 flex flex-wrap justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsAdHocDialogOpen(true)}
              className="gap-2"
            >
              <Clock className="h-4 w-4" />
              Add Hours
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsExpenseDialogOpen(true)}
              className="gap-2"
            >
              <Receipt className="h-4 w-4" />
              Add Expense
            </Button>
          </div>

          {weeklyGroups.length === 0 && rejectedExpenseGroups.length === 0 ? (
            <NeedsSubmissionEmptyState />
          ) : (
            <div className="space-y-8">
              {/* Time Entries to Submit */}
              {weeklyGroups.length > 0 && (
                <>
                  <TimeEntryWeekSelector
                    selectedWeek={selectedWeek}
                    onWeekChange={setSelectedWeek}
                    weeklyGroups={weeklyGroups}
                  />
                  {selectedGroup && (
                    <TimeEntryWeekForm
                      key={selectedGroup.weekKey}
                      weekGroup={selectedGroup}
                      onSubmitSuccess={() => {
                        refetchNeeds();
                        refetchPending();
                        const currentIndex = sortedWeekKeys.indexOf(
                          selectedGroup.weekKey,
                        );
                        if (currentIndex > 0) {
                          setSelectedWeek(sortedWeekKeys[currentIndex - 1]);
                        }
                      }}
                    />
                  )}
                </>
              )}

              {/* Rejected Expenses */}
              {rejectedExpenseGroups.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    Rejected Expenses
                  </h2>
                  <p className="text-sm text-slate-600 mb-4">
                    These expenses were rejected and need to be edited and
                    resubmitted or deleted.
                  </p>
                  <div className="space-y-3">
                    {rejectedExpenseGroups.flatMap((group) =>
                      group.expenses.map((expense) => (
                        <ExpenseCard
                          key={expense.expenseId}
                          expense={expense}
                          onEdit={handleEditExpense}
                          onDelete={handleDeleteExpense}
                          isDeleting={deletingExpenseId === expense.expenseId}
                        />
                      )),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "pending" && (
        <>
          {pendingGroups.length === 0 && pendingExpenseGroups.length === 0 ? (
            <EmptyState
              icon={<Clock className="h-10 w-10 text-amber-500" />}
              title="No Pending Entries"
              description="You don't have any time entries or expenses waiting for review. Submit your timesheets and they'll appear here."
            />
          ) : (
            <div className="space-y-6">
              {/* Pending Time Entries */}
              {pendingGroups.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Clock className="h-5 w-5 text-amber-500" />
                    Time Entries
                  </h2>
                  <div className="space-y-6">
                    {pendingGroups.map((group) => (
                      <div key={group.weekKey}>
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                          Week of{" "}
                          {format(new Date(group.weekStart), "MMM d, yyyy")}
                        </h3>
                        <div className="space-y-4">
                          {group.shifts.map((shift) => (
                            <SubmittedTimeEntryCard
                              key={shift.shiftId}
                              shift={shift}
                              onWithdraw={(shiftId) => {
                                setWithdrawingShiftId(shiftId);
                                withdrawEntry({ shiftId });
                              }}
                              isWithdrawing={
                                withdrawingShiftId === shift.shiftId
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Pending Expenses */}
              {pendingExpenseGroups.length > 0 && (
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Receipt className="h-5 w-5 text-amber-500" />
                    Expenses
                  </h2>
                  <div className="space-y-6">
                    {pendingExpenseGroups.map((group) => (
                      <div key={group.weekKey}>
                        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                          Week of{" "}
                          {format(new Date(group.weekStart), "MMM d, yyyy")}
                        </h3>
                        <div className="space-y-3">
                          {group.expenses.map((expense) => (
                            <ExpenseCard
                              key={expense.expenseId}
                              expense={expense}
                              onDelete={handleDeleteExpense}
                              isDeleting={
                                deletingExpenseId === expense.expenseId
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeTab === "history" && (
        <>
          {/* Week Navigator for History */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <div className="flex items-center rounded-xl border border-slate-400 bg-white p-1 shadow-xs">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Previous week"
                onClick={() => setHistoryWeek(getPreviousWeekKey(historyWeek))}
                className="h-9 w-9"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Next week"
                onClick={() => setHistoryWeek(getNextWeekKey(historyWeek))}
                disabled={isCurrentWeek(historyWeek, defaultTimezone)}
                className="h-9 w-9"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex min-h-12 flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm shadow-xs">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              <span
                className={cn(
                  "font-medium",
                  isCurrentWeek(historyWeek, defaultTimezone) && "text-primary",
                )}
              >
                {formatWeekRange(historyWeek)}
              </span>
              {isCurrentWeek(historyWeek, defaultTimezone) && (
                <Badge variant="secondary" className="text-xs">
                  This Week
                </Badge>
              )}
            </div>
          </div>

          {isLoadingHistory ? (
            <div className="flex flex-col justify-center items-center h-40 text-center">
              <Loader2 className="w-8 h-8 text-primary animate-spin mb-3" />
              <p className="text-sm text-slate-500">Loading entries...</p>
            </div>
          ) : historyGroups.length === 0 ||
            historyGroups[0]?.shifts.length === 0 ? (
            <EmptyState
              icon={<CheckCircle className="h-10 w-10 text-green-500" />}
              title="No Entries This Week"
              description="No approved time entries for this week. Use the arrows to browse other weeks."
            />
          ) : (
            <div className="space-y-4">
              {historyGroups[0]?.shifts.map((shift) => (
                <SubmittedTimeEntryCard key={shift.shiftId} shift={shift} />
              ))}
            </div>
          )}
        </>
      )}

      {/* Expense Submit Dialog - accessible from any tab */}
      <BatchExpenseSubmitDialog
        open={isExpenseDialogOpen}
        onOpenChange={setIsExpenseDialogOpen}
      />

      {/* Expense Edit Dialog - for rejected expenses */}
      <ExpenseEditDialog
        expense={editingExpense}
        open={editingExpense !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEditingExpense(null);
          }
        }}
      />

      {/* Ad-hoc Time Entry Dialog - accessible from any tab */}
      <AdHocTimeEntryDialog
        open={isAdHocDialogOpen}
        onOpenChange={setIsAdHocDialogOpen}
      />
    </div>
  );
}

function EmptyState({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center">
      <div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-slate-50">
        {icon}
      </div>
      <h2 className="text-xl font-semibold text-slate-900 mb-2">{title}</h2>
      <p className="text-base text-slate-500 max-w-md">{description}</p>
    </div>
  );
}

function NeedsSubmissionEmptyState() {
  const trpc = useTRPC();
  const { data: firstUpcomingShiftEnd } = useQuery(
    trpc.jobOffer.providerFirstUpcomingShiftEnd.queryOptions(),
  );

  const description = firstUpcomingShiftEnd
    ? `You don't have any time entries to submit yet. Check back after your shift ends on ${format(new Date(firstUpcomingShiftEnd), "EEEE, MMMM d 'at' h:mm a")}.`
    : "You don't have any time entries to submit. Your past shifts will appear here after they've ended.";

  return (
    <EmptyState
      icon={<FileText className="h-10 w-10 text-emerald-500" />}
      title="All Caught Up!"
      description={description}
    />
  );
}

export const Route = createFileRoute("/_provider/provider/jobs/timesheets/")({
  component: TimesheetsPage,
});
